import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IETicketService } from 'features/transaction-details/types';
import { CrossActionContext, TransactionViewContext } from 'features/transaction-details/types/transactionCrossContext';
import { getServiceByETicket } from 'features/transaction-details/services';
import { copyToClipboard, formatTransactionAmount } from 'features/transaction-details/helpers';
import { formatDateTimeToShowUser, getDetailOfDealOrCaseInCRM } from 'features/common/helpers';
import { RefundImpactNavigationButtons } from '../RefundImpactNavigationButtons';
import { AirLineLogo } from 'features/flightsExplorer/components/AirlineLogo';

interface ETicketDetailDialogProps {
	open: boolean;
	eticket: string;
	transactionContext?: TransactionViewContext;
	crossContext?: CrossActionContext;
	onClose: () => void;
	onCopySuccess: (message: string) => void;
	onRequestUnbind?: () => void;
	onNavigationError?: (message: string) => void;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }): JSX.Element => (
	<Box>
		<Typography variant="subtitle2" color="text.secondary">
			{label}
		</Typography>
		{children}
	</Box>
);

export const ETicketDetailDialog = ({
	open,
	eticket,
	transactionContext,
	crossContext,
	onClose,
	onCopySuccess,
	onRequestUnbind,
	onNavigationError,
}: ETicketDetailDialogProps): JSX.Element => {
	const [loading, setLoading] = useState(false);
	const [serviceInfo, setServiceInfo] = useState<IETicketService | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) {
			setServiceInfo(null);
			setError(null);
			return;
		}

		const load = async (): Promise<void> => {
			setLoading(true);
			setError(null);
			try {
				const response = await getServiceByETicket(eticket);
				if (response.data.elements.length > 0) {
					setServiceInfo(response.data.elements[0]);
				} else {
					setError('No se encontró información del servicio');
				}
			} catch {
				setError('Error al cargar el servicio');
			} finally {
				setLoading(false);
			}
		};

		void load();
	}, [open, eticket]);

	const handleCopyEticket = async (): Promise<void> => {
		const success = await copyToClipboard(eticket);
		if (success) onCopySuccess('E-Ticket copiado');
	};

	const handleCopyPnr = async (): Promise<void> => {
		if (!serviceInfo?.serviceReference) return;
		const success = await copyToClipboard(serviceInfo.serviceReference);
		if (success) onCopySuccess('PNR copiado');
	};

	const ticketDealId = serviceInfo?.saleOrderDealId;
	const sameDealAsTransaction =
		transactionContext != null && ticketDealId != null && ticketDealId === transactionContext.transactionDealId;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
				<ConfirmationNumberIcon color="primary" />
				<Box sx={{ flex: 1 }}>
					<Typography variant="h6" component="span">
						Detalles del E-Ticket
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Typography variant="body2" fontWeight="bold">
							{eticket}
						</Typography>
						<Tooltip title="Copiar E-Ticket">
							<IconButton size="small" onClick={() => void handleCopyEticket()}>
								<ContentCopyIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			</DialogTitle>

			<DialogContent dividers>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress />
					</Box>
				)}

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{serviceInfo && !loading && (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
						{transactionContext && ticketDealId != null && !sameDealAsTransaction && (
							<Alert severity="warning">
								Este boleto pertenece al caso de venta <strong>{ticketDealId}</strong>. La transacción que estás viendo
								está asociada al caso <strong>{transactionContext.transactionDealId}</strong>. Verificá que el cruce sea
								correcto.
							</Alert>
						)}

						{transactionContext && ticketDealId != null && sameDealAsTransaction && (
							<Chip label="Mismo caso de venta que la transacción" size="small" color="success" variant="outlined" />
						)}

						<Typography variant="subtitle1" fontWeight="bold">
							Boleto
						</Typography>
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
							{serviceInfo.vcCode && (
								<AirLineLogo code={serviceInfo.vcCode} name={serviceInfo.vcCode} showName={false} />
							)}
							<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, flex: 1 }}>
								<Field label="Pasajero">
									<Typography variant="body1" fontWeight="bold">
										{serviceInfo.passengerName}
									</Typography>
								</Field>
								<Field label="Estado">
									<Chip label={serviceInfo.status} size="small" color="success" />
								</Field>
								<Field label="GDS">
									<Typography variant="body2">{serviceInfo.gds}</Typography>
								</Field>
								<Field label="Validating Carrier">
									<Typography variant="body2">{serviceInfo.vcCode}</Typography>
								</Field>
								<Field label="Costo servicio">
									<Typography variant="body2">
										{formatTransactionAmount(serviceInfo.serviceCost, transactionContext?.coinCode ?? 'USD')}
									</Typography>
								</Field>
								<Field label="Tipo">
									<Typography variant="body2">{serviceInfo.ticketType}</Typography>
								</Field>
								<Field label="PNR">
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Typography variant="body2" fontWeight="bold" fontFamily="monospace">
											{serviceInfo.serviceReference}
										</Typography>
										<Tooltip title="Copiar PNR">
											<IconButton size="small" onClick={() => void handleCopyPnr()}>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								</Field>
							</Box>
						</Box>

						<Divider />

						<Typography variant="subtitle1" fontWeight="bold">
							Pertenece al caso de venta del boleto
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							Esta venta puede ser distinta del caso asociado a la transacción de la tarjeta.
						</Typography>
						<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
							<Field label="Caso de venta">
								<Typography variant="body1" fontWeight="bold">
									{ticketDealId}
								</Typography>
							</Field>
							<Field label="Vendedor (sale order)">
								<Typography variant="body2">{serviceInfo.saleOrderCreatedBy}</Typography>
							</Field>
							<Field label="Segmento">
								<Chip label={serviceInfo.saleOrderSegmentType} size="small" />
							</Field>
						</Box>
						{ticketDealId != null && (
							<RefundImpactNavigationButtons
								dealId={ticketDealId}
								saleOrderId={serviceInfo.saleOrderId}
								onError={onNavigationError}
							/>
						)}

						{serviceInfo.issueRequestCaseId != null && (
							<>
								<Divider />
								<Typography variant="subtitle1" fontWeight="bold">
									Caso de emisión
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
									<Typography variant="body1" fontWeight="bold">
										#{serviceInfo.issueRequestCaseId}
									</Typography>
									<Button
										size="small"
										variant="outlined"
										startIcon={<OpenInNewIcon />}
										href={getDetailOfDealOrCaseInCRM(serviceInfo.issueRequestCaseId, serviceInfo.saleOrderIsBitrixDeal)}
										target="_blank"
										rel="noopener noreferrer"
									>
										Ir al caso de emisión
									</Button>
								</Box>
								{serviceInfo.issueRequestCaseCreatedBy && (
									<Typography variant="caption" color="text.secondary">
										Creado por {serviceInfo.issueRequestCaseCreatedBy}
										{serviceInfo.issueRequestCaseCreatedAt
											? ` · ${formatDateTimeToShowUser(serviceInfo.issueRequestCaseCreatedAt)}`
											: ''}
									</Typography>
								)}
							</>
						)}

						{crossContext && (
							<>
								<Divider />
								<Typography variant="subtitle2" color="text.secondary">
									Cruce en esta transacción: {formatTransactionAmount(crossContext.amount, crossContext.coinCode)}{' '}
									(autorización #{crossContext.authorizationId})
								</Typography>
							</>
						)}
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
				<Button onClick={onClose}>Cerrar</Button>
				{crossContext?.canUnbind && onRequestUnbind && (
					<Button
						variant="outlined"
						color="warning"
						startIcon={<LinkOffIcon />}
						onClick={onRequestUnbind}
						disabled={loading}
					>
						Desvincular este boleto
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
